import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CompanyContext } from './useCompany';
import { useAuth } from '@/contexts/AuthContext';
import { useFeature } from '@/hooks/useFeature';
import { useNetworkStatus, checkNetworkStatus } from '@/hooks/useNetworkStatus';
import type { Company, MemberRole } from '@/types/database';
import { toast } from 'sonner';
import { cacheCompanies, getCachedCompanies } from '@/lib/offlineDataCache';
import { addToOfflineQueue } from '@/lib/offlineStorage';
import { deleteCompaniesCascade } from '@/lib/companyDeletion';
import { billableReleaseMessage } from '@/lib/quoteDeletion';
import { SelectActiveCompanyModal } from '@/components/modals/SelectActiveCompanyModal';

/**
 * A company row as returned when embedding the caller's membership role.
 * The `role` is stripped before storing in `companies` state (kept in
 * `roleByCompany`), but persisted in the offline cache so role survives reloads.
 */
type CompanyWithRole = Company & { role?: MemberRole };

/** Build a companyId -> role map from rows that carry an embedded role. */
function extractRoles(rows: CompanyWithRole[]): Record<string, MemberRole> {
  const map: Record<string, MemberRole> = {};
  for (const row of rows) {
    if (row.role) map[row.id] = row.role;
  }
  return map;
}

const ACTIVE_COMPANY_KEY = 'orcazap_active_company_id';

/**
 * Escolha de empresa ativa do plano grátis, gravada LOCALMENTE por usuário.
 *
 * A coluna `profiles.free_plan_active_company_id` continua sendo a memória entre
 * dispositivos, mas ela não pode ser pré-requisito para o modal fechar: o
 * `SelectActiveCompanyModal` é intencionalmente inescapável (sem X, sem Esc, sem
 * clique-fora), então depender de uma gravação de rede para sair dele trava o app
 * inteiro sem conexão. Este marcador local é o que faz a escolha valer na hora.
 * É escopado por usuário para não vazar entre contas em device compartilhado.
 */
const freePlanChoiceKey = (userId: string) => `orcamigo_free_plan_active_company:${userId}`;

function readFreePlanChoice(userId: string): string | null {
  try {
    return localStorage.getItem(freePlanChoiceKey(userId));
  } catch {
    return null;
  }
}

function writeFreePlanChoice(userId: string, companyId: string | null): void {
  try {
    if (companyId) localStorage.setItem(freePlanChoiceKey(userId), companyId);
    else localStorage.removeItem(freePlanChoiceKey(userId));
  } catch {
    // Cota cheia / storage bloqueado: a escolha ainda vale nesta sessão.
  }
}

/** Teto de tempo para o await, para um fetch pendurado não travar a UI. */
const MAX_SAVE_MS = 8000;

/**
 * Tenta persistir a escolha no servidor. Devolve `false` (sem lançar) quando
 * está offline, quando estoura o tempo ou quando a RLS/rede recusa — o chamador
 * já aplicou a escolha localmente e não deve ser bloqueado por isto.
 */
async function persistFreePlanChoice(userId: string, companyId: string): Promise<boolean> {
  if (!(await checkNetworkStatus())) return false;
  try {
    const result = await Promise.race([
      supabase.from('profiles').update({ free_plan_active_company_id: companyId }).eq('id', userId),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), MAX_SAVE_MS)),
    ]);
    if (!result) return false; // estourou o tempo
    if (result.error) {
      console.error('Error saving free-plan active company:', result.error);
      return false;
    }
    return true;
  } catch (e) {
    console.error('Error saving free-plan active company:', e);
    return false;
  }
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user, profile, refreshProfile } = useAuth();
  const multipleCompanies = useFeature('multiple_companies');
  // Usado para retomar a gravação da escolha do plano grátis assim que a rede
  // voltar (o import já existia no arquivo, mas o hook nunca era chamado).
  const { isOnline } = useNetworkStatus();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roleByCompany, setRoleByCompany] = useState<Record<string, MemberRole>>({});
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_COMPANY_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  // Uma tentativa de criar a empresa padrão POR SESSÃO (D14). Sem o ref, um
  // erro da RPC faria cada refetch tentar de novo, e o `fetchCompanies` roda em
  // toda troca de foco.
  const ensuredDefaultRef = useRef(false);
  const [showSelectCompanyModal, setShowSelectCompanyModal] = useState(false);
  const [savingSelection, setSavingSelection] = useState(false);

  // Derive active company from list
  const activeCompany = companies.find(c => c.id === activeCompanyId) || companies[0] || null;

  // Role of the current user within the active company. Server is the source of
  // truth; this is the last-known value (also persisted in the offline cache).
  const currentRole = activeCompany ? roleByCompany[activeCompany.id] ?? null : null;

  // The declarative feature (including user overrides) is authoritative here.
  const canCreateCompany = multipleCompanies.allowed || companies.length < 1;

  const fetchCompanies = useCallback(async () => {
    if (!user) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    // Only flip the global `loading` gate on a genuine cold load (nothing on
    // screen yet). If we already have companies rendered, refresh silently in
    // the background — flipping `loading` here would make consumers that gate on
    // it (e.g. CompanyList's early-return spinner) unmount their subtree,
    // discarding transient UI like an open, half-filled CompanyFormModal. This
    // is what caused the "form resets after switching browser tabs" bug.
    const cached = getCachedCompanies(user.id) as CompanyWithRole[] | null;
    if (companies.length === 0) {
      if (cached && cached.length > 0) {
        setCompanies(cached as Company[]);
        setRoleByCompany(extractRoles(cached));
        setLoading(false);
      } else {
        setLoading(true);
      }
    }

    try {
      // Team accounts: a company is visible if the user has a membership in it
      // (owner OR invited member), not only if they own it. The embedded
      // membership row carries this user's role in that company.
      const { data, error } = await supabase
        .from('companies')
        .select('*, company_members!inner(role)')
        .eq('company_members.user_id', user.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching companies:', error);
        // If offline and we have cached data, don't clear
        if (!cached || cached.length === 0) {
          setCompanies([]);
        }
      } else {
        // Flatten `company_members: [{ role }]` into a `role` field per company.
        const rows: CompanyWithRole[] = (data || []).map((row: any) => {
          const { company_members, ...company } = row;
          const role: MemberRole | undefined = Array.isArray(company_members)
            ? company_members[0]?.role
            : company_members?.role;
          return { ...(company as Company), role };
        });

        // REDE DE SEGURANÇA DO D14. Uma conta SEM empresa não consegue criar
        // orçamento, cliente nem item de catálogo: a RLS de team accounts exige
        // `company_id` gravável (#4) e `is_company_member(null, uid)` é falso.
        // O caminho normal é o trigger do cadastro (`handle_new_user`), mas ele
        // engole o próprio erro de propósito — falhar ali derrubaria o CADASTRO
        // INTEIRO. Esta é a segunda camada, e é ela que conserta quem já está
        // sem empresa hoje.
        //
        // Só entra no ramo da leitura BEM-SUCEDIDA (o `else` deste `if`): uma
        // falha de rede devolve zero linhas e criaria uma empresa a cada
        // reconexão. A RPC é idempotente (`where not exists` num statement só),
        // então duas telas do mesmo usuário não produzem duas empresas.
        if (rows.length === 0 && !ensuredDefaultRef.current) {
          ensuredDefaultRef.current = true;
          const { error: ensureError } = await supabase.rpc('ensure_default_company', {});
          if (ensureError) {
            console.error('Error ensuring default company:', ensureError);
          } else {
            // Relê em vez de montar a linha na mão: o servidor decide o nome, e
            // o `role` vem do embed de `company_members`, que o trigger cria.
            const { data: retry } = await supabase
              .from('companies')
              .select('*, company_members!inner(role)')
              .eq('company_members.user_id', user.id)
              .order('created_at', { ascending: true });
            for (const row of (retry || []) as any[]) {
              const { company_members, ...company } = row;
              const role: MemberRole | undefined = Array.isArray(company_members)
                ? company_members[0]?.role
                : company_members?.role;
              rows.push({ ...(company as Company), role });
            }
          }
        }

        setCompanies(rows as Company[]);
        setRoleByCompany(extractRoles(rows));
        // Cache for offline access (role included so it survives reloads).
        if (rows.length > 0) {
          cacheCompanies(user.id, rows);
        }
      }
    } catch (e) {
      console.error('Unexpected error fetching companies:', e);
      // If offline and we have cached data, don't clear
      if (!cached || cached.length === 0) {
        setCompanies([]);
      }
    } finally {
      setLoading(false);
    }
    // Depend on user?.id (a stable string), NOT the `user` object. supabase-js
    // re-emits SIGNED_IN/TOKEN_REFRESHED when the tab regains focus, handing us a
    // fresh User object for the SAME user; keying on the object would recreate
    // this callback and re-run the fetch on every tab focus for no reason.
  }, [user?.id, companies.length]);

  // Fetch companies when user changes
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Listen for company-changed events to reload from cache (for offline creation)
  useEffect(() => {
    const handleCompanyChanged = () => {
      // Reload from cache to get any offline-created companies
      if (user) {
        const cached = getCachedCompanies(user.id) as CompanyWithRole[] | null;
        if (cached && cached.length > 0) {
          setCompanies(cached as Company[]);
          setRoleByCompany(extractRoles(cached));
        }
      }
    };
    
    window.addEventListener('company-changed', handleCompanyChanged);
    return () => window.removeEventListener('company-changed', handleCompanyChanged);
  }, [user]);

  // When companies load, ensure we have a valid active company
  useEffect(() => {
    if (companies.length > 0 && !companies.find(c => c.id === activeCompanyId)) {
      // Active company not found, default to first
      setActiveCompanyIdState(companies[0].id);
      localStorage.setItem(ACTIVE_COMPANY_KEY, companies[0].id);
    }
  }, [companies, activeCompanyId]);

  // Detect downgrade to Free with multiple companies. Choice is persisted to
  // profiles.free_plan_active_company_id in Supabase so the modal appears only
  // once per downgrade cycle (reset to NULL when the user upgrades back to PRO).
  useEffect(() => {
    if (!user || loading || !profile) return;

    if (!multipleCompanies.allowed && companies.length > 1) {
      // O marcador local vale tanto quanto a coluna do servidor: sem isso, uma
      // escolha feita offline traria o modal (inescapável) de volta no próximo
      // start ainda sem conexão, prendendo o usuário fora do app.
      const storedChoice = profile.free_plan_active_company_id ?? readFreePlanChoice(user.id);
      const chosenCompany = storedChoice
        ? companies.find(c => c.id === storedChoice)
        : null;

      if (chosenCompany) {
        // Already picked a company for this free-plan cycle — enforce it as
        // active and keep the modal closed.
        if (activeCompanyId !== chosenCompany.id) {
          setActiveCompanyIdState(chosenCompany.id);
          localStorage.setItem(ACTIVE_COMPANY_KEY, chosenCompany.id);
        }
        setShowSelectCompanyModal(false);
      } else {
        setShowSelectCompanyModal(true);
      }
    } else {
      // Voltou para o PRO (ou só tem uma empresa): o ciclo de plano grátis
      // terminou, então o marcador local sai junto com a coluna do servidor —
      // senão um downgrade futuro herdaria uma escolha velha em silêncio.
      if (multipleCompanies.allowed) writeFreePlanChoice(user.id, null);
      setShowSelectCompanyModal(false);
    }
  }, [user, multipleCompanies.allowed, companies, loading, profile, activeCompanyId]);

  /**
   * Sobe para o servidor a escolha que só existe localmente (feita offline ou
   * com a gravação estourando o tempo). Roda quando a rede volta (`isOnline`) e a
   * cada carga de perfil. `syncedChoiceRef` evita repetir a tentativa para o
   * mesmo id — sem ele, o `refreshProfile` do sucesso re-dispara o efeito.
   */
  const syncedChoiceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !profile || profile.free_plan_active_company_id || !isOnline) return;
    const local = readFreePlanChoice(user.id);
    if (!local || syncedChoiceRef.current === local) return;

    let cancelled = false;
    syncedChoiceRef.current = local;
    (async () => {
      const ok = await persistFreePlanChoice(user.id, local);
      if (ok && !cancelled) await refreshProfile();
      else if (!ok) syncedChoiceRef.current = null; // deixa tentar de novo depois
    })();
    return () => {
      cancelled = true;
    };
  }, [user, profile, refreshProfile, isOnline]);

  /**
   * Aplica a escolha LOCALMENTE primeiro e só depois tenta gravar no servidor.
   *
   * A ordem importa: este modal não tem como ser fechado pelo usuário, então
   * enquanto o `await` da gravação não voltasse o botão ficava em "Salvando..."
   * e o app inteiro ficava inacessível — foi o que aconteceu sem conexão, onde o
   * fetch não completa e o `finally` nunca rodava. A fonte de verdade da empresa
   * ativa já é o estado + localStorage; a coluna no servidor só serve para o
   * modal não reaparecer, e agora é best-effort (com teto de tempo), sincronizada
   * na próxima vez que houver conexão.
   */
  const handleSelectActiveCompany = useCallback(async (companyId: string) => {
    if (!user) return;
    setSavingSelection(true);
    try {
      writeFreePlanChoice(user.id, companyId);
      setActiveCompanyIdState(companyId);
      localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
      window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
      setShowSelectCompanyModal(false);

      if (await persistFreePlanChoice(user.id, companyId)) {
        syncedChoiceRef.current = companyId;
        await refreshProfile();
      } else {
        toast.info('Escolha aplicada. Vamos salvá-la no servidor quando houver conexão.');
      }
    } finally {
      setSavingSelection(false);
    }
  }, [user, refreshProfile]);

  const setActiveCompanyId = useCallback((id: string) => {
    setActiveCompanyIdState(id);
    localStorage.setItem(ACTIVE_COMPANY_KEY, id);
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: id } }));
  }, []);

  const refreshCompanies = useCallback(async () => {
    await fetchCompanies();
  }, [fetchCompanies]);

  const createCompany = useCallback(async (
    companyData: Omit<Company, 'id' | 'user_id' | 'created_at'>
  ): Promise<Company | null> => {
    if (!user) {
      toast.warning('Você precisa estar logado');
      return null;
    }

    // PRO users can create unlimited companies
    // Only check limit for non-premium users
    if (!multipleCompanies.allowed && companies.length >= 1) {
      toast.warning('Limite do plano gratuito: 1 empresa. Faça upgrade para PRO!');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('companies')
        .insert({
          user_id: user.id,
          name: companyData.name,
          cnpj_cpf: companyData.cnpj_cpf,
          email: companyData.email,
          phone: companyData.phone,
          address: companyData.address,
          logo_url: companyData.logo_url,
          signature_url: companyData.signature_url,
          primary_color: companyData.primary_color || '#10B981',
          pix_key: companyData.pix_key,
          pix_city: companyData.pix_city,
          default_terms: companyData.default_terms,
          // ARMADILHA #33: este payload é montado por LISTA EXPLÍCITA de
          // colunas. Coluna nova em `companies` que não seja citada AQUI e no
          // `updateCompany` abaixo salva com "sucesso" (sem erro de RLS, sem
          // erro de tipo) e nunca chega ao banco.
          default_payment_methods: companyData.default_payment_methods,
          week_starts_on: companyData.week_starts_on,
          pdf_template: companyData.pdf_template || 'classico',
          // `?? true` e não `|| true`: `false` é uma escolha VÁLIDA do usuário
          // (não quer a linha de assinatura no cupom de balcão) e `||` a
          // transformaria de volta em `true` silenciosamente.
          pdv_signature_line: companyData.pdv_signature_line ?? true,
          logo_watermark: companyData.logo_watermark ?? false,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating company:', {
          code: (error as any)?.code,
          message: (error as any)?.message,
          details: (error as any)?.details,
          hint: (error as any)?.hint,
        });

        const isLimitOrRls =
          (error as any)?.code === '42501' ||
          (error as any)?.message?.includes('row-level security') ||
          (error as any)?.message?.includes('tier_limit');

        // If the server is blocking and the user is PRO, this is a backend policy/trigger issue.
        if (isLimitOrRls && multipleCompanies.allowed) {
          toast.error('Servidor bloqueou a criação apesar do PRO. Precisa ajustar a policy/trigger no banco.');
        } else if (isLimitOrRls) {
          toast.warning('Limite do plano gratuito: 1 empresa. Faça upgrade para PRO!');
        } else {
          toast.error('Erro ao criar empresa');
        }

        return null;
      }

      await fetchCompanies();
      toast.success('Empresa criada com sucesso!');
      return data as Company;
    } catch (e) {
      console.error('Unexpected error creating company:', e);
      toast.error('Erro ao criar empresa');
      return null;
    }
  }, [user, multipleCompanies.allowed, companies.length, fetchCompanies]);

  const updateCompany = useCallback(async (
    id: string,
    companyData: Partial<Company>
  ): Promise<boolean> => {
    if (!user) {
      toast.warning('Você precisa estar logado');
      return false;
    }

    try {
      const { error } = await supabase
        .from('companies')
        .update({
          name: companyData.name,
          cnpj_cpf: companyData.cnpj_cpf,
          email: companyData.email,
          phone: companyData.phone,
          address: companyData.address,
          logo_url: companyData.logo_url,
          signature_url: companyData.signature_url,
          primary_color: companyData.primary_color,
          pix_key: companyData.pix_key,
          pix_city: companyData.pix_city,
          default_terms: companyData.default_terms,
          default_payment_methods: companyData.default_payment_methods,
          week_starts_on: companyData.week_starts_on,
          pdf_template: companyData.pdf_template,
          pdv_signature_line: companyData.pdv_signature_line,
          logo_watermark: companyData.logo_watermark,
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating company:', error);
        toast.error('Erro ao atualizar empresa');
        return false;
      }

      await fetchCompanies();
      toast.success('Empresa atualizada!');
      return true;
    } catch (e) {
      console.error('Unexpected error updating company:', e);
      toast.error('Erro ao atualizar empresa');
      return false;
    }
  }, [user, fetchCompanies]);

  const deleteCompany = useCallback(async (id: string): Promise<boolean> => {
    if (!user) {
      toast.warning('Você precisa estar logado');
      return false;
    }

    // Check if we're online
    const online = await checkNetworkStatus();

    // Helper to update local state immediately
    const removeLocalCompany = () => {
      const remaining = companies.filter(c => c.id !== id);
      setCompanies(remaining);
      cacheCompanies(user.id, remaining);
      
      // If we deleted the active company, switch to another (or clear)
      if (activeCompanyId === id) {
        if (remaining.length > 0) {
          setActiveCompanyId(remaining[0].id);
        } else {
          setActiveCompanyIdState(null);
          localStorage.removeItem(ACTIVE_COMPANY_KEY);
          window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: null } }));
        }
      }
      
      window.dispatchEvent(new CustomEvent('companies-updated'));
    };

    if (!online) {
      // Offline mode: queue deletion and update UI immediately
      addToOfflineQueue('delete_company', { companyId: id, userId: user.id });
      removeLocalCompany();
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      toast.success('Empresa excluída localmente! Será sincronizado quando houver internet.', {
        icon: '📴',
      });
      return true;
    }

    try {
      // PELA PORTA ÚNICA (ver `src/lib/companyDeletion.ts`). O delete cru falhava
      // com 23502 enquanto a FK era SET NULL numa coluna NOT NULL, e mesmo com o
      // CASCADE ele ainda pode ser recusado com 23514 quando uma despesa
      // cobrável aponta para um orçamento desta empresa — a RPC solta a
      // marcação antes, na mesma transação.
      const { deleted, released } = await deleteCompaniesCascade([id]);

      if (deleted === 0) {
        // A RPC ignora empresa de que o usuário não é DONO. Zero aqui não é
        // sucesso vazio: é recusa, e dizer "excluída" seria a mentira do #173.
        toast.warning('Só o dono da empresa pode excluí-la.');
        return false;
      }

      removeLocalCompany();
      const aviso = billableReleaseMessage(released);
      toast.success(aviso ? `Empresa excluída! ${aviso}` : 'Empresa excluída!');
      return true;
    } catch (e) {
      console.error('Unexpected error deleting company:', e);
      toast.error('Erro ao excluir empresa');
      return false;
    }
  }, [user, companies, activeCompanyId, setActiveCompanyId]);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        activeCompany,
        loading,
        currentRole,
        roleByCompany,
        setActiveCompanyId,
        refreshCompanies,
        createCompany,
        updateCompany,
        deleteCompany,
        canCreateCompany,
      }}
    >
      {children}
      {user && (
        <SelectActiveCompanyModal
          open={showSelectCompanyModal}
          companies={companies}
          activeCompanyId={activeCompanyId}
          onSelectCompany={handleSelectActiveCompany}
          saving={savingSelection}
        />
      )}
    </CompanyContext.Provider>
  );
}
